// src/services/videoStorageService.js
// Service abstrait pour le stockage vidéo — actuellement Cloudflare R2
// Pour changer de provider, modifier uniquement ce fichier

import { 
  S3Client, 
  PutObjectCommand, 
  DeleteObjectCommand, 
  GetObjectCommand,
  CreateMultipartUploadCommand,
  UploadPartCommand,
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand,
} from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

// ─── Configuration R2 ───────────────────────────────────────────────────────
const R2_ENDPOINT    = process.env.REACT_APP_R2_ENDPOINT;
const R2_ACCESS_KEY  = process.env.REACT_APP_R2_ACCESS_KEY_ID;
const R2_SECRET_KEY  = process.env.REACT_APP_R2_SECRET_ACCESS_KEY;
const R2_BUCKET      = process.env.REACT_APP_R2_BUCKET_NAME;

// 🔍 Debug — vérifier que les variables sont bien lues
console.log('🔧 R2 Config:', {
  endpoint: R2_ENDPOINT ? '✅ défini' : '❌ manquant',
  accessKey: R2_ACCESS_KEY ? '✅ défini' : '❌ manquant',
  secretKey: R2_SECRET_KEY ? '✅ défini' : '❌ manquant',
  bucket: R2_BUCKET ? `✅ ${R2_BUCKET}` : '❌ manquant',
});

const s3Client = new S3Client({
  region: 'auto',
  endpoint: R2_ENDPOINT,
  credentials: {
    accessKeyId:     R2_ACCESS_KEY,
    secretAccessKey: R2_SECRET_KEY,
  },
  forcePathStyle: true, // Requis pour Cloudflare R2
});

// ─── Upload vidéo (Multipart — supporte les fichiers volumineux) ──────────────
/**
 * Upload un fichier vidéo vers R2 via multipart upload
 * Découpe le fichier en morceaux de 10 MB — supporte les fichiers > 10 GB
 * @param {File}     file       - Fichier vidéo à uploader
 * @param {string}   userId     - ID de l'utilisateur
 * @param {string}   matchId    - ID du match (optionnel)
 * @param {Function} onProgress - Callback progression (0-100)
 * @returns {{ storagePath, storageUrl }}
 */
export const uploadVideo = async (file, userId, matchId = null, onProgress = null) => {
  const CHUNK_SIZE  = 10 * 1024 * 1024; // 10 MB par partie
  const timestamp   = Date.now();
  const safeName    = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const folder      = matchId ? `${userId}/${matchId}` : `${userId}/unlinked`;
  const storagePath = `videos/${folder}/${timestamp}_${safeName}`;

  console.log('📤 Multipart upload vers R2:', storagePath);
  console.log(`   Taille: ${(file.size / 1024 / 1024 / 1024).toFixed(2)} GB`);

  // 1. Initier le multipart upload
  const createCmd = new CreateMultipartUploadCommand({
    Bucket:      R2_BUCKET,
    Key:         storagePath,
    ContentType: file.type || 'video/mp4',
    Metadata: {
      userId:   userId,
      matchId:  matchId || '',
      fileName: safeName,
    },
  });
  const { UploadId } = await s3Client.send(createCmd);
  console.log('   UploadId:', UploadId);

  const parts    = [];
  const numParts = Math.ceil(file.size / CHUNK_SIZE);

  try {
    // 2. Uploader chaque partie
    for (let i = 0; i < numParts; i++) {
      const start  = i * CHUNK_SIZE;
      const end    = Math.min(start + CHUNK_SIZE, file.size);
      const chunk  = file.slice(start, end);
      const buffer = await chunk.arrayBuffer();

      const uploadPartCmd = new UploadPartCommand({
        Bucket:     R2_BUCKET,
        Key:        storagePath,
        UploadId,
        PartNumber: i + 1,
        Body:       buffer,
      });

      const { ETag } = await s3Client.send(uploadPartCmd);
      parts.push({ PartNumber: i + 1, ETag });

      // Progression
      const pct = Math.round(((i + 1) / numParts) * 100);
      console.log(`   Partie ${i + 1}/${numParts} — ${pct}%`);
      if (onProgress) onProgress(pct);
    }

    // 3. Finaliser le multipart upload
    const completeCmd = new CompleteMultipartUploadCommand({
      Bucket:          R2_BUCKET,
      Key:             storagePath,
      UploadId,
      MultipartUpload: { Parts: parts },
    });
    await s3Client.send(completeCmd);

  } catch (err) {
    // En cas d'erreur, annuler le multipart upload pour ne pas laisser de fragments
    console.error('❌ Erreur multipart upload, annulation...', err);
    await s3Client.send(new AbortMultipartUploadCommand({
      Bucket:   R2_BUCKET,
      Key:      storagePath,
      UploadId,
    }));
    throw err;
  }

  const storageUrl = `${R2_ENDPOINT}/${storagePath}`;
  console.log('✅ Multipart upload terminé:', storageUrl);

  return { storagePath, storageUrl };
};

// ─── Upload clip (point) ─────────────────────────────────────────────────────
/**
 * Upload un clip vidéo d'un point spécifique
 * @param {Blob}   blob        - Blob vidéo du clip
 * @param {string} userId      - ID utilisateur
 * @param {string} matchId     - ID du match
 * @param {number} pointNumber - Numéro du point
 * @returns {{ storagePath, storageUrl }}
 */
export const uploadClip = async (blob, userId, matchId, pointNumber) => {
  const storagePath = `clips/${userId}/${matchId}/point_${String(pointNumber).padStart(3, '0')}.mp4`;

  console.log('📤 Upload clip R2:', storagePath);

  const command = new PutObjectCommand({
    Bucket:      R2_BUCKET,
    Key:         storagePath,
    Body:        blob,
    ContentType: 'video/mp4',
  });

  await s3Client.send(command);

  const storageUrl = `${R2_ENDPOINT}/${storagePath}`;

  console.log('✅ Clip uploadé:', storageUrl);

  return { storagePath, storageUrl };
};

// ─── Générer URL signée (lecture sécurisée) ──────────────────────────────────
/**
 * Génère une URL temporaire signée pour lire une vidéo privée
 * @param {string} storagePath - Chemin dans R2
 * @param {number} expiresIn   - Durée de validité en secondes (défaut: 1h)
 * @returns {string} URL signée
 */
export const getSignedVideoUrl = async (storagePath, expiresIn = 3600) => {
  const command = new GetObjectCommand({
    Bucket: R2_BUCKET,
    Key:    storagePath,
  });

  const signedUrl = await getSignedUrl(s3Client, command, { expiresIn });
  return signedUrl;
};

// ─── Supprimer une vidéo ─────────────────────────────────────────────────────
/**
 * Supprime un fichier dans R2
 * @param {string} storagePath - Chemin dans R2
 */
export const deleteVideo = async (storagePath) => {
  console.log('🗑️ Suppression R2:', storagePath);

  const command = new DeleteObjectCommand({
    Bucket: R2_BUCKET,
    Key:    storagePath,
  });

  await s3Client.send(command);
  console.log('✅ Fichier supprimé:', storagePath);
};

// ─── Obtenir l'URL publique ──────────────────────────────────────────────────
/**
 * Retourne l'URL publique d'un fichier (si le bucket a un domaine public)
 * @param {string} storagePath
 * @returns {string}
 */
export const getPublicUrl = (storagePath) => {
  return `${R2_ENDPOINT}/${storagePath}`;
};

const videoStorageService = {
  uploadVideo,
  uploadClip,
  getSignedVideoUrl,
  deleteVideo,
  getPublicUrl,
};

export default videoStorageService;